"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import {
  LifecycleStage, STAGE_LABEL, STAGE_EMOJI, STAGE_COLOR, STAGE_BG,
  SavedKeyword, ACTIVITY_LEVELS,
} from "@/types";
import { UserLevelBadge } from "@/components/community/UserLevelBadge";
import { UserInfoModal } from "@/components/mypage/UserInfoModal";
import { useBetaStatus } from "@/hooks/useBetaStatus";

// 마이그 050 (2026-05-05) — lite/pro 통합 → paid
const TIER_LABEL: Record<string, string> = {
  free_public: "무료",
  free_auth: "무료 회원",
  paid: "유료",
  franchise: "프랜차이즈",
};

const TIER_COLOR: Record<string, string> = {
  free_public: "#B0B0B0",
  free_auth: "#B0B0B0",
  paid: "#FF5C35",
  franchise: "#4ADE80",
};

export default function MypagePage() {
  const { isLoggedIn, user, token, logout } = useAuthStore();
  const router = useRouter();
  // 2026-05-22 — 베타 모드 상태 (구독 카드 / 구독 관리 link 자동 노출)
  const beta = useBetaStatus();
  const betaEnded = beta ? !beta.beta_active : false;

  const [saved, setSaved] = useState<SavedKeyword[] | null>(null);
  // 메뉴 항목별 unread 배지 (어디 알림인지 직관 표시)
  const [counts, setCounts] = useState<{ mention: number; friend: number; dm: number; support: number }>({
    mention: 0, friend: 0, dm: 0, support: 0,
  });

  useEffect(() => {
    if (!isLoggedIn) router.replace("/login");
  }, [isLoggedIn, router]);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios.get("/api/me/saved", { headers }).then((r) => setSaved(r.data)).catch(() => setSaved([]));
    // unread count 4종 병행 fetch (mention/friend/dm + support 답변)
    Promise.all([
      axios.get<{ count: number }>("/api/community/mentions/unread-count", { headers }).catch(() => ({ data: { count: 0 } })),
      axios.get<{ count: number }>("/api/friends/notifications/unread-count", { headers }).catch(() => ({ data: { count: 0 } })),
      axios.get<{ count: number }>("/api/dm/unread-count", { headers }).catch(() => ({ data: { count: 0 } })),
      axios.get<{ unread: number }>("/api/support/unread-count", { headers }).catch(() => ({ data: { unread: 0 } })),
    ]).then(([m, f, d, s]) => {
      setCounts({
        mention: m.data.count || 0,
        friend: f.data.count || 0,
        dm: d.data.count || 0,
        support: s.data.unread || 0,
      });
    });
  }, [token]);

  const removeSaved = async (id: number) => {
    if (!token) return;
    await axios.delete(`/api/me/saved/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setSaved((prev) => prev?.filter((s) => s.id !== id) ?? null);
  };

  if (!user) return null;

  const tier = user.tier ?? "free_auth";

  return (
    <div className="px-4 py-6 pb-24">
      <h1 className="text-xl font-black text-text mb-6">마이페이지</h1>

      {/* 프로필 카드 */}
      <ProfileCard tier={tier} />

      {/* R3-33 — 온보딩 진행도 카드 (5단계 모두 끝나기 전까지 표시) */}
      <OnboardingCard />


      {/* 관리자 전용 — 별도 카드 (is_admin 만 노출) */}
      {user?.is_admin && (
        <Link href="/admin">
          <div className="card mb-4 border-primary/40 bg-primary/5 flex items-center gap-3 cursor-pointer hover:bg-primary/10 transition-colors">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center text-lg">
              ⚙️
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-primary">관리자 대시보드</p>
              <p className="text-2xs text-text-sub mt-0.5">
                커버리지 · 수집 현황 · 모더레이션
              </p>
            </div>
            <span className="text-primary text-sm">→</span>
          </div>
        </Link>
      )}

      {/* 프랜차이즈 본사 운영자 전용 — 마이그 093 (2026-05-23) */}
      {user?.franchise_role === "hq" && (
        <Link href="/franchise">
          <div className="card mb-4 border-amber-500/40 bg-amber-500/5 flex items-center gap-3 cursor-pointer hover:bg-amber-500/10 transition-colors">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center text-lg">
              🏢
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-500">본사 관리</p>
              <p className="text-2xs text-text-sub mt-0.5">
                가맹점 list · 1회용 코드 · brand 정보
              </p>
            </div>
            <span className="text-amber-500 text-sm">→</span>
          </div>
        </Link>
      )}

      {/* 구독 상태 — 2026-05-22 마이그 092: useBetaStatus 기반 자동 노출
          (어드민 /admin/beta-mode 에서 end_date 변경 시 즉시 반영, 5분 cache TTL) */}
      {betaEnded && ["free_public", "free_auth"].includes(tier) && (
        <Link href="/mypage/subscribe" className="block card mb-4 border-primary/30 hover:border-primary transition-colors">
          <p className="text-sm font-semibold text-text mb-1">⭐ 유료 플랜으로 업그레이드</p>
          <p className="text-xs text-text-sub">전체 기능 + 광고 없음 — 월 3,900원</p>
        </Link>
      )}

      {/* 저장한 트렌드 */}
      <section className="card mb-4">
        <h2 className="text-sm font-bold text-text mb-3">⭐ 저장한 트렌드</h2>
        {saved === null ? (
          <p className="text-xs text-text-sub py-2">불러오는 중...</p>
        ) : saved.length === 0 ? (
          <p className="text-xs text-text-sub py-2">
            아직 저장된 트렌드가 없어요. 트렌드 상세 페이지에서 ☆ 버튼을 눌러 저장해보세요.
          </p>
        ) : (
          <div className="space-y-2">
            {saved.map((s) => {
              const stage = s.lifecycle_stage as LifecycleStage | null;
              const stageColor = stage ? STAGE_COLOR[stage] : "#B0B0B0";
              const stageBg = stage ? STAGE_BG[stage] : "rgba(136,136,136,0.15)";
              const stageLabel = stage ? `${STAGE_EMOJI[stage]} ${STAGE_LABEL[stage]}` : "미집계";
              return (
                <div
                  key={s.id}
                  className="flex items-center gap-2 p-2 rounded-xl bg-bg-secondary"
                >
                  <button
                    onClick={() => s.keyword_id && router.push(`/trend/${s.keyword_id}`)}
                    disabled={!s.keyword_id}
                    className="flex-1 min-w-0 text-left disabled:opacity-60"
                  >
                    <p className="text-sm font-semibold text-text truncate">{s.keyword_text}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span
                        className="text-2xs font-bold px-1.5 py-0.5 rounded-full"
                        style={{ color: stageColor, background: stageBg }}
                      >
                        {stageLabel}
                      </span>
                      {/* 룰 v4 (2026-05-07): 점수 X, 30일 멘션 영향력 표시 */}
                      {s.mention_count !== null && s.mention_count !== undefined ? (
                        <span className="text-2xs text-text-sub">
                          30일 {s.mention_count.toLocaleString()}건
                        </span>
                      ) : (
                        <span className="text-2xs text-text-sub">오늘자 데이터 없음</span>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={() => removeSaved(s.id)}
                    className="text-text-sub hover:text-primary text-lg"
                    aria-label="저장 해제"
                  >
                    ★
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* 메뉴 — 4개 카테고리 (β-8 N5 사용자 피드백). 이모지 제거 2026-05-15 */}
      {(
        [
          ["소통", [
            ["받은 멘션", "/mypage/mentions"],
            ["친구", "/mypage/friends"],
            ["DM", "/mypage/dm"],
            ["내 게시글", "/mypage/posts"],
            ["⭐ 북마크", "/mypage/bookmarks"],
          ]],
          ["서비스", [
            ["AI 비서 (전체보기)", "/chat"],
            ["AI 이미지 스튜디오", "/image-studio"],
            ["SNS 글작성", "/sns-writer"],
            ["키워드 분석 요청", "/mypage/keyword-request"],
            ["고객 문의", "/mypage/support"],
          ]],
          ["계정", [
            ["알림 설정", "/mypage/notifications"],
            // 2026-05-22 마이그 092 — useBetaStatus 기반 자동 노출
            ...(betaEnded ? [["구독 관리", "/mypage/subscribe"] as [string, string]] : []),
          ]],
          ["약관·안내", [
            ["사용 가이드", "/guide"],
            ["이용약관", "/terms"],
            ["개인정보처리방침", "/privacy"],
          ]],
        ] as [string, [string, string][]][]
      ).map(([section, items]) => (
        <div key={section} className="mb-3">
          <p className="text-2xs font-bold text-text-sub uppercase tracking-wider px-1 mb-2">{section}</p>
          <div className="card divide-y divide-border">
            {items.map(([label, href]) => {
              // 메뉴 항목별 unread 배지 (사용자가 어디 알림인지 직관 인지)
              const badge =
                label === "받은 멘션" ? counts.mention :
                label === "친구"      ? counts.friend  :
                label === "DM"        ? counts.dm      :
                label === "고객 문의" ? counts.support : 0;
              return (
                <Link key={label} href={href}>
                  <div className="flex items-center py-3.5 first:pt-0 last:pb-0 gap-3 hover:text-primary transition-colors">
                    <span className="text-sm font-medium text-text flex-1">{label}</span>
                    {badge > 0 && (
                      <span className="min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-bg text-2xs font-bold flex items-center justify-center">
                        {badge > 99 ? "99+" : badge}
                      </span>
                    )}
                    <span className="text-text-sub">→</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}

      {/* 로그아웃 */}
      <button
        onClick={() => {
          logout();
          router.replace("/");
        }}
        className="w-full mt-4 py-3 text-sm font-semibold text-text-sub border border-border rounded-2xl hover:bg-bg-elevated transition-colors"
      >
        로그아웃
      </button>
    </div>
  );
}

/* ───────────── 프로필 카드 (R2-4 — 닉네임 inline 편집) ───────────── */

function ProfileCard({ tier }: { tier: string }) {
  const { user, token, setUser } = useAuthStore();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(user?.nickname ?? "");
  const [saving, setSaving] = useState(false);
  const [checkState, setCheckState] = useState<"idle" | "checking" | "ok" | "duplicate" | "self">("idle");
  // #18 (2026-05-15) — 배지 chip 닉네임 옆 + chip 클릭 inline popover 안내
  const [badges, setBadges] = useState<string[]>([]);
  const [infoOpen, setInfoOpen] = useState<"level" | "badge" | null>(null);
  useEffect(() => {
    if (!token) return;
    axios.get<string[]>("/api/me/badges", {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => setBadges(r.data)).catch(() => setBadges([]));
  }, [token]);

  // 실시간 중복 검사 (debounce 400ms)
  useEffect(() => {
    if (!editing || !token) return;
    const trimmed = draft.trim();
    if (!trimmed) { setCheckState("idle"); return; }
    setCheckState("checking");
    const t = setTimeout(async () => {
      try {
        const r = await axios.get<{ available: boolean; reason: string }>(
          `/api/auth/me/nickname/check?nickname=${encodeURIComponent(trimmed)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (r.data.reason === "self") setCheckState("self");
        else setCheckState(r.data.available ? "ok" : "duplicate");
      } catch {
        setCheckState("idle");
      }
    }, 400);
    return () => clearTimeout(t);
  }, [draft, editing, token]);

  if (!user) return null;

  const startEdit = () => {
    setDraft(user.nickname ?? "");
    setEditing(true);
    setCheckState("idle");
  };
  const save = async () => {
    if (!token) return;
    const trimmed = draft.trim();
    if (!trimmed || trimmed.length > 50) return;
    if (checkState === "duplicate") {
      alert("이미 사용 중인 닉네임이에요.");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.patch(
        "/api/auth/me/nickname",
        { nickname: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser(r.data);
      setEditing(false);
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 409) alert("이미 사용 중인 닉네임이에요.");
      else alert("닉네임 변경 실패");
    } finally {
      setSaving(false);
    }
  };

  const initial = (user.nickname || user.email || "?")[0].toUpperCase();

  return (
    <div className="card mb-4">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-black text-lg">
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  maxLength={50}
                  placeholder="닉네임 (50자 이내)"
                  className={`flex-1 min-w-0 bg-bg-secondary text-sm text-text px-2 py-1 rounded-lg border outline-none ${
                    checkState === "duplicate" ? "border-red-400" :
                    checkState === "ok" ? "border-green-400" : "border-border focus:border-primary/50"
                  }`}
                  autoFocus
                />
                <button
                  onClick={save}
                  disabled={saving || !draft.trim() || checkState === "duplicate" || checkState === "checking"}
                  className="text-xs font-bold text-primary disabled:opacity-30"
                >
                  저장
                </button>
                <button onClick={() => setEditing(false)} className="text-xs text-text-sub">취소</button>
              </div>
              {checkState === "checking" && <p className="text-2xs text-text-sub">확인 중…</p>}
              {checkState === "ok" && <p className="text-2xs text-green-400">✓ 사용 가능</p>}
              {checkState === "self" && <p className="text-2xs text-text-sub">현재 닉네임</p>}
              {checkState === "duplicate" && <p className="text-2xs text-red-400">이미 사용 중인 닉네임이에요</p>}
            </div>
          ) : (
            <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
              <p className="text-sm font-semibold text-text truncate">
                {user.nickname ? `@${user.nickname}` : "닉네임 미설정"}
              </p>
              {/* 등급 chip — 클릭 시 안내 (#18) */}
              <button
                onClick={() => setInfoOpen(infoOpen === "level" ? null : "level")}
                title="등급 안내"
                className="shrink-0"
              >
                <UserLevelBadge
                  level={user.activity_level}
                  label={user.activity_label}
                  emoji={user.activity_emoji}
                />
              </button>
              {/* 배지 chip — 클릭 시 조건 안내 (#18) */}
              {badges.length > 0 && (
                <button
                  onClick={() => setInfoOpen(infoOpen === "badge" ? null : "badge")}
                  title="배지 조건"
                  className="text-2xs px-1.5 py-0.5 rounded-md bg-bg-elevated border border-border text-text shrink-0 hover:border-primary/40"
                >
                  🎖 {badges.length}
                </button>
              )}
              <button onClick={startEdit} className="text-2xs text-primary shrink-0 hover:underline">
                수정
              </button>
            </div>
          )}

          {/* 등급·배지 안내 — 인라인 expand → 모달 (사용자 결정 2026-05-17) */}
          <UserInfoModal kind={infoOpen} badges={badges} onClose={() => setInfoOpen(null)} />
          {/* 운영자·등급 라벨 — 베타 가격 숨김 정책 (2026-05-15) → '(유료 베타)' 제거 */}
          {user.is_admin ? (
            <span className="text-xs font-bold inline-block mt-0.5" style={{ color: "#FFD93D" }}>
              👑 운영자
            </span>
          ) : (
            <span
              className="text-xs font-bold mt-0.5 inline-block"
              style={{ color: TIER_COLOR[tier] }}
            >
              {TIER_LABEL[tier] ?? tier}
            </span>
          )}
          {/* 이메일 — 본인 확인용. 회색 톤다운 (2026-05-15 옵션 A) */}
          <p className="text-2xs text-text-sub/60 mt-1 truncate">{user.email}</p>
        </div>
      </div>

      {/* 활동 점수 progress (마이그 035) */}
      <ActivityProgress />

      {/* 자기소개 편집 (#27 마이그 041) */}
      <BioEditor />
    </div>
  );
}

function BioEditor() {
  const { token } = useAuthStore();
  const [bio, setBio] = useState("");
  const [profilePublic, setProfilePublic] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get<{ bio: string | null; profile_public: boolean }>("/api/me/profile", {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      setBio(r.data.bio ?? "");
      setProfilePublic(r.data.profile_public);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [token]);

  const save = async () => {
    if (!token) return;
    setSaving(true);
    try {
      await axios.patch("/api/me/profile",
        { bio: bio.trim(), profile_public: profilePublic },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;
  return (
    <div className="mt-3 pt-3 border-t border-border">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-2xs text-text-sub">소개</p>
        {!editing && (
          <button onClick={() => setEditing(true)} className="text-2xs text-primary hover:underline">
            {bio ? "수정" : "+ 작성"}
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="예: 강남 디저트 카페 운영 5년차. 마카롱·휘낭시에 전문."
            maxLength={200}
            rows={3}
            className="w-full text-2xs bg-bg-elevated border border-border rounded-md px-2 py-1.5 text-text resize-none"
          />
          <label className="flex items-start gap-2 text-2xs text-text-sub">
            <input
              type="checkbox"
              checked={profilePublic}
              onChange={(e) => setProfilePublic(e.target.checked)}
              className="w-3.5 h-3.5 mt-0.5"
            />
            <span className="leading-relaxed">
              회원 리스트에 내 프로필 공개 (라이트+ 회원만 볼 수 있음)
              <br />
              <span className="text-text-muted">
                ⓘ 끄면 닉네임이 <span className="font-semibold">@익명_XXXX</span> 로 마스킹되고 자기소개 / DM 비활성화. 명예 차트 (TOP 5) 에서도 제외돼요.
              </span>
            </span>
          </label>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="flex-1 text-2xs py-1.5 rounded bg-primary text-bg font-bold disabled:opacity-30">
              {saving ? "저장 중..." : "저장"}
            </button>
            <button onClick={() => setEditing(false)} className="text-2xs text-text-sub px-3">취소</button>
          </div>
          <p className="text-2xs text-text-sub">{bio.length} / 200</p>
        </div>
      ) : (
        <p className="text-2xs text-text leading-relaxed whitespace-pre-wrap">
          {bio || <span className="text-text-sub">자기소개가 없어요. 다른 사장님께 인사 한마디 적어보세요.</span>}
        </p>
      )}
    </div>
  );
}

function ActivityProgress() {
  const { user } = useAuthStore();
  // 2026-05-15 #18 — 배지·등급 안내는 ProfileCard 의 chip 클릭 popover 로 이동.
  // 여기는 progress bar + 점수 + 다음 단계 만 (심플).
  if (!user) return null;
  const score = user.activity_score ?? 0;
  const level = user.activity_level ?? 1;
  const cur = ACTIVITY_LEVELS.find((l) => l.level === level) ?? ACTIVITY_LEVELS[0];
  const next = ACTIVITY_LEVELS.find((l) => l.level === level + 1);
  const span = next ? next.min - cur.min : 0;
  const inLevel = score - cur.min;
  const pct = next && span > 0 ? Math.max(0, Math.min(100, (inLevel / span) * 100)) : 100;
  return (
    <div className="mt-3 pt-3 border-t border-border">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-2xs text-text-sub">
          활동 점수 <span className="text-text font-bold">{score}점</span>
        </span>
        <span className="text-2xs text-text-sub">
          {next ? `Lv.${next.level} ${next.label} 까지 ${Math.max(0, next.min - score)}점` : "최고 등급 달성"}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-bg-elevated overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: cur.color }}
        />
      </div>
    </div>
  );
}



// R3-33 — 온보딩 진행도 카드
interface OnboardingOut {
  days_since_signup: number;
  has_store: boolean;
  has_menu: boolean;
  has_stock_in: boolean;
  has_sale_log: boolean;
  has_image_request: boolean;
  completed_steps: number;
  progress_pct: number;
  next_action_key: string;
  next_action_label: string;
  next_action_href: string;
  next_action_hint: string;
}

function OnboardingCard() {
  const { token } = useAuthStore();
  const [data, setData] = useState<OnboardingOut | null>(null);

  useEffect(() => {
    if (!token) return;
    axios
      .get<OnboardingOut>("/api/me/onboarding", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [token]);

  if (!data) return null;
  // 5단계 모두 완료 + 7일 지난 사용자에겐 카드 숨김 (이미 정착)
  if (data.completed_steps === 5 && data.days_since_signup > 7) return null;

  const STEPS = [
    { key: "has_store", icon: "🏪", label: "매장 등록", done: data.has_store },
    { key: "has_menu", icon: "☕", label: "메뉴 셋팅", done: data.has_menu },
    { key: "has_stock_in", icon: "📦", label: "첫 입고", done: data.has_stock_in },
    { key: "has_sale_log", icon: "📝", label: "첫 판매 입력", done: data.has_sale_log },
    { key: "has_image_request", icon: "🎨", label: "AI 이미지", done: data.has_image_request },
  ];

  return (
    <div className="card mb-4 border-primary/30 bg-primary/5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-bold text-text">📋 시작하기 ({data.completed_steps}/5)</p>
          <p className="text-2xs text-text-sub mt-0.5">
            가입 {data.days_since_signup}일차 — 차근차근 매장 셋팅
          </p>
        </div>
        <span className="text-xs font-bold text-primary">{data.progress_pct}%</span>
      </div>

      {/* progress bar */}
      <div className="w-full bg-bg-elevated rounded h-1.5 overflow-hidden mb-3">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${data.progress_pct}%` }}
        />
      </div>

      {/* 5 step icons */}
      <div className="flex items-center justify-between mb-3 px-1">
        {STEPS.map((s) => (
          <div key={s.key} className="flex flex-col items-center gap-1">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-base transition-colors ${
              s.done
                ? "bg-primary/20 text-text"
                : "bg-bg-elevated text-text-sub opacity-50"
            }`}>
              {s.done ? "✓" : s.icon}
            </div>
            <p className="text-2xs text-text-sub">{s.label}</p>
          </div>
        ))}
      </div>

      {/* 다음 액션 CTA */}
      {data.next_action_key !== "all_done" ? (
        <Link href={data.next_action_href} className="block">
          <div className="bg-primary text-bg rounded-md p-3 hover:bg-primary/90 transition-colors">
            <p className="text-sm font-bold">{data.next_action_label} →</p>
            <p className="text-2xs opacity-90 mt-0.5">{data.next_action_hint}</p>
          </div>
        </Link>
      ) : (
        <div className="bg-primary/10 border border-primary/30 rounded-md p-3 text-center">
          <p className="text-sm font-bold text-primary">{data.next_action_label}</p>
          <p className="text-2xs text-text-sub mt-0.5">{data.next_action_hint}</p>
        </div>
      )}
    </div>
  );
}
